import { Component, Prop, h, State, Element } from '@stencil/core';

/**
 * Video component with lazy loading and autoplay support
 * @example
 *   <me-video src="https://videos.pexels.com/video-files/4904755/4904755-hd_1920_1080_24fps.mp4" lazy="true"></me-video>
 */
@Component({
  tag: 'me-video',
  styleUrl: 'index.scss',
  shadow: false,
})
export class MeVideo {
  /**
   * Video source URL
   */
  @Prop() src!: string;
  /**
   * Poster image for the video
   */
  @Prop() poster?: string;
  /**
   * Autoplay video
   */
  @Prop() autoplay: boolean = true;
  /**
   * Mute video
   */
  @Prop() muted: boolean = true;
  /**
   * Loop video
   */
  @Prop() loop: boolean = true;
  /**
   * Play inline (for mobile)
   */
  @Prop() playsinline: boolean = true;
  /**
   * Lazy load video (default: true). Use lazy="false" to disable.
   */
  @Prop() lazy: boolean | string = true;

  @State() isInViewport: boolean = false;
  @Element() el: HTMLElement;
  private observer?: IntersectionObserver;

  get isLazy(): boolean {
    if (typeof this.lazy === 'string') {
      return this.lazy !== 'false';
    }
    return !!this.lazy;
  }

  componentWillLoad() {
    if (!this.isLazy) {
      this.isInViewport = true;
    }
  }

  componentDidLoad() {
    if (this.isLazy) {
      this.observer = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting) {
            this.isInViewport = true;
            this.observer?.disconnect();
          }
        },
        { threshold: 0.1 },
      );
      this.observer.observe(this.el);
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  render() {
    if (!this.isInViewport) {
      return this.poster ? (
        <img src={this.poster} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} alt="Video preview" />
      ) : (
        <div style={{ width: '100%', height: '100%' }}></div>
      );
    }
    return (
      // eslint-disable-next-line jsx-a11y/media-has-caption
      <video
        src={this.src}
        poster={this.poster}
        autoplay={this.autoplay}
        muted={this.muted}
        loop={this.loop}
        playsinline={this.playsinline}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      ></video>
    );
  }
}
